import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import {
  Sparkle, MagicWand, ImageSquare, Cards, IdentificationCard, PencilLine,
  ArrowsOutCardinal, FileArrowDown, FunnelSimple, Stack, ClockCounterClockwise,
  UploadSimple, DownloadSimple, FloppyDisk, Camera, ClipboardText, Copy, TrashSimple,
  Scan, Sun, ArrowsClockwise, ArrowsInLineHorizontal, GridFour, Printer,
} from "@phosphor-icons/react";
import { toast } from "sonner";

// --------------- Sidebar categories ---------------
const CATEGORIES = [
  { id: "ai",      label: "AI Enhance",       icon: MagicWand,       hint: "HD · Upscale · Face · Blur" },
  { id: "bg",      label: "Background",       icon: ImageSquare,     hint: "Remove · Colour · Passport" },
  { id: "id",      label: "ID / Gov Presets", icon: IdentificationCard, hint: "Aadhaar · PAN · Passport" },
  { id: "editor",  label: "Photo Editor",     icon: PencilLine,      hint: "Crop · Rotate · Adjust" },
  { id: "filter",  label: "Filters",          icon: FunnelSimple,    hint: "B&W · Sepia · HDR" },
  { id: "resize",  label: "Resize · Compress",icon: ArrowsOutCardinal, hint: "Pixel · mm · Target KB" },
  { id: "layout",  label: "Passport Layout",  icon: GridFour,        hint: "4×6 · A4 sheets" },
  { id: "batch",   label: "Batch Process",    icon: Stack,           hint: "Bulk enhance / convert" },
  { id: "scan",    label: "Scan · OCR",       icon: Scan,            hint: "Deskew · Extract text" },
  { id: "history", label: "History",          icon: ClockCounterClockwise, hint: "Today's jobs" },
];

// AI tools (fal.ai)
const AI_TOOLS = [
  { id: "hd_enhance",       label: "AI HD Enhancement" },
  { id: "upscale_2x",       label: "Super Resolution 2×" },
  { id: "upscale_4x",       label: "Super Resolution 4×" },
  { id: "upscale_8x",       label: "Super Resolution 8×" },
  { id: "face_restore",     label: "Face Enhancement" },
  { id: "portrait_retouch", label: "Portrait Retouch" },
  { id: "restore_old",      label: "Restore Old Photo" },
  { id: "document_cleanup", label: "AI Document Cleanup" },
  { id: "remove_blur",      label: "Remove Blur" },
  { id: "remove_noise",     label: "Remove Noise" },
  { id: "sharpen",          label: "Sharpen (AI)" },
];

const BG_COLORS = [
  { key: "white", label: "White",  css: "#ffffff" },
  { key: "blue",  label: "Passport Blue", css: "#6495ed" },
  { key: "red",   label: "Red",    css: "#d02e2e" },
  { key: "grey",  label: "Light Grey", css: "#c8c8c8" },
  { key: "black", label: "Black",  css: "#000000" },
];

const COMPRESS_TARGETS = [20, 50, 100, 200, 500, 1024];

// ==================================================================
// Page
// ==================================================================
export default function AIPhotoEnhancer() {
  const navigate = useNavigate();
  const [cat, setCat] = useState("ai");
  const [source, setSource] = useState("");
  const [output, setOutput] = useState("");
  const [busy, setBusy] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("_none");
  const [presets, setPresets] = useState([]);
  const [historyStats, setHistoryStats] = useState({ total: 0, by_tool: {}, recent: [] });
  const [history, setHistory] = useState([]);       // local recent outputs, {src,out,tool}
  const fileRef = useRef(null);

  // Load customer list + presets + backend stats
  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/photo/gov-presets").then((r) => setPresets(r.data)).catch(() => {});
    api.get("/photo/history").then((r) => setHistoryStats(r.data)).catch(() => {});
  }, []);

  const loadImageFile = (file) => {
    if (!file) return;
    if (file.size > 15_000_000) { toast.error("Max 15 MB"); return; }
    const r = new FileReader();
    r.onload = () => { setSource(r.result); setOutput(""); };
    r.readAsDataURL(file);
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    loadImageFile(e.dataTransfer.files?.[0]);
  };

  const onPaste = useCallback((e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.type?.startsWith("image/")) { loadImageFile(it.getAsFile()); break; }
    }
  }, []);
  useEffect(() => { window.addEventListener("paste", onPaste); return () => window.removeEventListener("paste", onPaste); }, [onPaste]);

  const downloadOutput = async () => {
    if (!output) return;
    try {
      const r = await fetch(output); const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `enhanced-${Date.now()}.${blob.type.includes("jpeg") ? "jpg" : "png"}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const saveToCustomer = async () => {
    if (!output) { toast.error("Enhance first"); return; }
    if (customerId === "_none") { toast.error("Pick a customer"); return; }
    try {
      await api.post("/photo/save-to-customer", { image: output, customer_id: customerId });
      toast.success("Saved to customer profile");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const swap = () => { setSource(output); setOutput(""); };

  const pushHistory = (tool) => setHistory((h) => [{ tool, src: source, out: output, at: Date.now() }, ...h].slice(0, 12));

  // ================= Top action-row (Dashboard Quick Actions) =================
  const goTo = (t) => setCat(t);

  return (
    <div className="max-w-[1560px] mx-auto p-4 lg:p-6" data-testid="ai-photo-enhancer">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <Sparkle size={14} weight="fill" className="text-orange-500" />
            <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500">Premium AI Photo Enhancer</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Photo Studio · All-in-One</h1>
          <p className="text-sm text-slate-500 mt-1">HD Enhance · Passport &amp; ID · Government presets · Smart compress · Batch · OCR · Print — everything for your CSC.</p>
        </div>
        <div className="hidden md:flex gap-1.5 flex-wrap justify-end">
          {[
            { l: "AI Enhance", t: "ai", i: MagicWand },
            { l: "Passport Photo", t: "id", i: IdentificationCard },
            { l: "Remove BG", t: "bg", i: ImageSquare },
            { l: "Resize", t: "resize", i: ArrowsOutCardinal },
            { l: "PDF Tools", t: "pdf", i: FileArrowDown, ext: "/pdf-studio" },
            { l: "Print", t: "layout", i: Printer },
          ].map((q) => (
            <button
              key={q.l}
              data-testid={`qa-${q.t}`}
              onClick={() => q.ext ? navigate(q.ext) : goTo(q.t)}
              className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-white border border-slate-200 hover:border-orange-400 hover:text-orange-600 text-xs font-medium text-slate-700 transition-colors"
            >
              <q.i size={12} /> {q.l}
            </button>
          ))}
        </div>
      </div>

      {/* Three-column layout */}
      <div className="grid grid-cols-12 gap-4">
        {/* Left sidebar */}
        <Card className="col-span-12 lg:col-span-2 p-2 bg-white border-slate-200" data-testid="tool-rail">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold px-2 pt-2 pb-1">Tools</div>
          <div className="space-y-0.5">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setCat(c.id)}
                data-testid={`rail-${c.id}`}
                className={`w-full text-left flex items-start gap-2 px-2 py-2 rounded-md transition-all ${cat === c.id ? "bg-orange-50 border border-orange-200" : "hover:bg-slate-50 border border-transparent"}`}
              >
                <c.icon size={16} weight={cat === c.id ? "fill" : "regular"} className={cat === c.id ? "text-orange-600 mt-0.5" : "text-slate-500 mt-0.5"} />
                <div className="flex-1 min-w-0">
                  <div className={`text-xs font-semibold ${cat === c.id ? "text-orange-900" : "text-slate-800"}`}>{c.label}</div>
                  <div className="text-[10px] text-slate-500 truncate">{c.hint}</div>
                </div>
              </button>
            ))}
            <button
              onClick={() => navigate("/pdf-studio")}
              data-testid="rail-pdf"
              className="w-full text-left flex items-start gap-2 px-2 py-2 rounded-md hover:bg-slate-50 border border-transparent"
            >
              <FileArrowDown size={16} className="text-slate-500 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-slate-800">PDF Tools</div>
                <div className="text-[10px] text-slate-500">Merge · Split · OCR PDF</div>
              </div>
            </button>
          </div>
        </Card>

        {/* Center — Source + Output viewer */}
        <div className="col-span-12 lg:col-span-7 space-y-4">
          <Card className="p-3 bg-white border-slate-200" onDrop={onDrop} onDragOver={(e) => e.preventDefault()}>
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Preview · Before / After</div>
              <div className="flex items-center gap-1.5">
                <label className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-full border border-slate-300 text-xs font-medium text-slate-700 hover:bg-slate-50 cursor-pointer" data-testid="btn-upload">
                  <UploadSimple size={12} /> Upload
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => loadImageFile(e.target.files?.[0])} />
                </label>
                <label className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-full border border-slate-300 text-xs font-medium text-slate-700 hover:bg-slate-50 cursor-pointer" data-testid="btn-camera">
                  <Camera size={12} /> Camera
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => loadImageFile(e.target.files?.[0])} />
                </label>
                <Button size="sm" variant="ghost" className="h-8 text-xs gap-1" onClick={() => { setSource(""); setOutput(""); }} data-testid="btn-clear"><TrashSimple size={12} /> Clear</Button>
                {output && (
                  <>
                    <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={swap} data-testid="btn-use-output"><ArrowsClockwise size={12} /> Use output</Button>
                    <Button size="sm" className="h-8 text-xs gap-1 bg-orange-600 hover:bg-orange-700 text-white" onClick={downloadOutput} data-testid="btn-download"><DownloadSimple size={12} /> Download</Button>
                  </>
                )}
              </div>
            </div>

            {!source && !output ? (
              <button
                onClick={() => fileRef.current?.click()}
                className="w-full h-[520px] rounded-md border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-orange-400 hover:text-orange-500 transition-colors"
                data-testid="dropzone"
              >
                <UploadSimple size={44} weight="duotone" className="mb-3" />
                <div className="text-base font-semibold text-slate-500">Drop image, click to browse, or paste (Ctrl+V)</div>
                <div className="text-xs mt-1 text-slate-400">JPG / PNG / WEBP · up to 15 MB</div>
              </button>
            ) : (
              <BeforeAfterSlider before={source} after={output || source} height={520} />
            )}
          </Card>

          {/* Save to customer */}
          {output && (
            <Card className="p-3 bg-white border-slate-200 flex flex-wrap items-center gap-3" data-testid="save-panel">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Save to</div>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger className="w-64 h-9" data-testid="save-customer"><SelectValue placeholder="Choose customer…" /></SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="_none">— Choose customer —</SelectItem>
                  {customers.slice(0, 400).map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.mobile}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" onClick={saveToCustomer} disabled={customerId === "_none"} className="bg-slate-900 hover:bg-slate-800 text-white h-9 gap-1.5" data-testid="btn-save-customer">
                <FloppyDisk size={12} /> Save to Profile
              </Button>
              <div className="text-[10px] text-slate-500 italic ml-auto">Saved images appear in Customer → Documents.</div>
            </Card>
          )}
        </div>

        {/* Right — Contextual controls */}
        <div className="col-span-12 lg:col-span-3 space-y-4">
          <Card className="p-4 bg-white border-slate-200 min-h-[300px]" data-testid={`panel-${cat}`}>
            {cat === "ai" &&      <AIEnhancePanel  source={source} setOutput={setOutput} busy={busy} setBusy={setBusy} onDone={(t) => pushHistory(t)} />}
            {cat === "bg" &&      <BackgroundPanel source={source} setOutput={setOutput} busy={busy} setBusy={setBusy} />}
            {cat === "id" &&      <GovPresetPanel  source={source} setOutput={setOutput} busy={busy} setBusy={setBusy} presets={presets} />}
            {cat === "editor" &&  <EditorPanel     source={source} setOutput={setOutput} busy={busy} setBusy={setBusy} />}
            {cat === "filter" &&  <FilterPanel     source={source} setOutput={setOutput} busy={busy} setBusy={setBusy} />}
            {cat === "resize" &&  <ResizePanel     source={source} setOutput={setOutput} busy={busy} setBusy={setBusy} />}
            {cat === "layout" &&  <LayoutPanel     source={source} navigate={navigate} />}
            {cat === "batch" &&   <BatchLink       navigate={navigate} />}
            {cat === "scan" &&    <ScanLink        navigate={navigate} />}
            {cat === "history" && <HistoryPanel    stats={historyStats} recent={history} />}
          </Card>
        </div>
      </div>
    </div>
  );
}

// ==================================================================
// AI Enhance panel
// ==================================================================
function AIEnhancePanel({ source, setOutput, busy, setBusy, onDone }) {
  const [tool, setTool] = useState("hd_enhance");

  const run = async () => {
    if (!source) { toast.error("Upload a photo first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/photo/enhance", { tool, image_base64: source });
      setOutput(data.output_url);
      toast.success("Enhanced");
      onDone?.(tool);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">AI Enhancement</div>
      <div className="space-y-1">
        {AI_TOOLS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTool(t.id)}
            data-testid={`ai-tool-${t.id}`}
            className={`w-full text-left px-3 py-2 rounded-md text-xs font-medium transition-all ${tool === t.id ? "bg-orange-50 border border-orange-300 text-orange-900" : "bg-white border border-slate-200 text-slate-700 hover:border-orange-300"}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <Button onClick={run} disabled={busy || !source} className="w-full h-10 bg-orange-600 hover:bg-orange-700 text-white gap-2" data-testid="ai-run">
        <Sparkle size={14} weight="fill" /> {busy ? "AI thinking…" : "Enhance"}
      </Button>
      <div className="text-[10px] text-slate-500 italic">Powered by fal.ai · a few ₹ per image.</div>
    </div>
  );
}

// ==================================================================
// Background panel — remove / colour composite / passport bg
// ==================================================================
function BackgroundPanel({ source, setOutput, busy, setBusy }) {
  const [color, setColor] = useState("white");
  const [custom, setCustom] = useState("#ffffff");
  const [transparent, setTransparent] = useState(false);

  const remove = async () => {
    if (!source) { toast.error("Upload a photo first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/photo/enhance", { tool: "bg_remove", image_base64: source });
      setOutput(data.output_url); toast.success("Background removed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const composite = async () => {
    if (!source) { toast.error("Upload a photo first"); return; }
    setBusy(true);
    try {
      const chosen = color === "custom" ? custom : color;
      const { data } = await api.post("/photo/bg-color", { image: source, color: chosen, already_transparent: transparent });
      setOutput(data.image);
      toast.success(`Background: ${chosen}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Background Studio</div>
      <Button onClick={remove} disabled={busy || !source} variant="outline" className="w-full gap-2 h-10" data-testid="bg-remove">
        <ImageSquare size={12} /> Remove Background (AI)
      </Button>
      <div className="border-t border-slate-100 my-2" />
      <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Fill Background Colour</Label>
      <div className="grid grid-cols-5 gap-1.5" data-testid="bg-colors">
        {BG_COLORS.map((c) => (
          <button
            key={c.key}
            onClick={() => setColor(c.key)}
            title={c.label}
            data-testid={`bg-color-${c.key}`}
            className={`h-9 rounded-md border-2 transition-all ${color === c.key ? "border-orange-500 scale-105" : "border-slate-200 hover:border-slate-400"}`}
            style={{ background: c.css }}
          />
        ))}
        <button
          onClick={() => setColor("custom")}
          title="Custom"
          data-testid="bg-color-custom"
          className={`h-9 rounded-md border-2 flex items-center justify-center text-[10px] font-bold transition-all ${color === "custom" ? "border-orange-500 scale-105 bg-gradient-to-br from-red-500 via-yellow-400 to-blue-500 text-white" : "border-slate-200 bg-gradient-to-br from-red-400 via-yellow-300 to-blue-400 text-white/90"}`}
        >
          ###
        </button>
      </div>
      {color === "custom" && (
        <Input type="color" value={custom} onChange={(e) => setCustom(e.target.value)} className="h-9 w-full p-1 cursor-pointer" data-testid="bg-color-picker" />
      )}
      <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
        <input type="checkbox" checked={transparent} onChange={(e) => setTransparent(e.target.checked)} className="w-4 h-4" data-testid="bg-transparent" />
        Image is already transparent (skip AI remove)
      </label>
      <Button onClick={composite} disabled={busy || !source} className="w-full h-10 bg-orange-600 hover:bg-orange-700 text-white gap-2" data-testid="bg-apply">
        Apply Background
      </Button>
    </div>
  );
}

// ==================================================================
// Government / ID presets panel
// ==================================================================
function GovPresetPanel({ source, setOutput, busy, setBusy, presets }) {
  const [pid, setPid] = useState("");
  const [applyBg, setApplyBg] = useState(true);
  const [transparent, setTransparent] = useState(false);
  const [meta, setMeta] = useState(null);

  const run = async () => {
    if (!source) { toast.error("Upload a photo first"); return; }
    if (!pid) { toast.error("Pick a preset"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/photo/gov-preset", { image: source, preset_id: pid, apply_bg: applyBg, already_transparent: transparent });
      setOutput(data.image);
      setMeta({ size_kb: data.size_kb, preset: data.preset, achieved: data.achieved });
      toast.success(`${data.preset.label} · ${data.size_kb} KB`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const groups = {
    id: presets.filter((p) => p.category === "id"),
    portal: presets.filter((p) => p.category === "portal"),
    sign: presets.filter((p) => p.category === "sign"),
  };

  return (
    <div className="space-y-3" data-testid="gov-preset-panel">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Government / ID Presets</div>
      <div className="text-[11px] text-slate-500">Auto-formats to correct dimensions, background &amp; file-size.</div>

      {Object.entries(groups).map(([grp, list]) => list.length > 0 && (
        <div key={grp}>
          <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mt-2 mb-1">
            {grp === "id" ? "Identity Cards" : grp === "portal" ? "Portal Uploads" : "Signature"}
          </div>
          <div className="space-y-1">
            {list.map((p) => (
              <button
                key={p.id}
                onClick={() => setPid(p.id)}
                data-testid={`preset-${p.id}`}
                className={`w-full text-left px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-all leading-tight ${pid === p.id ? "bg-orange-50 border border-orange-300 text-orange-900" : "bg-white border border-slate-200 text-slate-700 hover:border-orange-300"}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      ))}

      <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
        <input type="checkbox" checked={applyBg} onChange={(e) => setApplyBg(e.target.checked)} className="w-4 h-4" data-testid="preset-apply-bg" />
        Auto-apply passport background colour
      </label>
      {applyBg && (
        <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer -mt-2">
          <input type="checkbox" checked={transparent} onChange={(e) => setTransparent(e.target.checked)} className="w-3.5 h-3.5" />
          Image is already transparent
        </label>
      )}
      <Button onClick={run} disabled={busy || !source || !pid} className="w-full h-10 bg-orange-600 hover:bg-orange-700 text-white gap-2" data-testid="preset-apply">
        <IdentificationCard size={14} weight="fill" /> {busy ? "Formatting…" : "Apply Preset"}
      </Button>
      {meta && (
        <div className="mt-2 p-2 rounded-md bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-900 space-y-0.5">
          <div><span className="font-semibold">{meta.preset.label}</span></div>
          <div>Output size: <span className="font-semibold">{meta.size_kb} KB</span> {meta.achieved ? "✓ within target" : "· above target (best-effort)"}</div>
          <div>Dimensions: {meta.preset.w_px ? `${meta.preset.w_px}×${meta.preset.h_px} px` : `${meta.preset.w_mm}×${meta.preset.h_mm} mm @ ${meta.preset.dpi} DPI`}</div>
        </div>
      )}
    </div>
  );
}

// ==================================================================
// Photo Editor — crop-rotate-flip + adjustments
// ==================================================================
function EditorPanel({ source, setOutput, busy, setBusy }) {
  const [b, setB] = useState(100), [c, setC] = useState(100), [s, setS] = useState(100);
  const [sh, setSh] = useState(100), [ex, setEx] = useState(0);
  const [rot, setRot] = useState(0), [flipH, setFlipH] = useState(false), [flipV, setFlipV] = useState(false), [straight, setStraight] = useState(0);

  const runAdjust = async () => {
    if (!source) { toast.error("Upload first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/photo/adjust", {
        image: source, brightness: b / 100, contrast: c / 100, saturation: s / 100, sharpness: sh / 100, exposure: ex / 100,
      });
      setOutput(data.image); toast.success("Adjustments applied");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const runTransform = async () => {
    if (!source) { toast.error("Upload first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/photo/transform", { image: source, rotate: rot, flip_h: flipH, flip_v: flipV, straighten_deg: straight });
      setOutput(data.image); toast.success("Transformed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const reset = () => { setB(100); setC(100); setS(100); setSh(100); setEx(0); };

  const Row = ({ label, val, setVal, min = 20, max = 200, testid }) => (
    <div>
      <div className="flex items-center justify-between text-[11px] mb-0.5">
        <span className="text-slate-600">{label}</span>
        <span className="font-mono text-slate-800">{val}%</span>
      </div>
      <Slider value={[val]} min={min} max={max} step={1} onValueChange={(v) => setVal(v[0])} className="mt-0.5" data-testid={testid} />
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Adjustments</div>
      <Row label="Brightness" val={b} setVal={setB} testid="adj-brightness" />
      <Row label="Contrast"   val={c} setVal={setC} testid="adj-contrast" />
      <Row label="Saturation" val={s} setVal={setS} testid="adj-saturation" />
      <Row label="Sharpness"  val={sh} setVal={setSh} testid="adj-sharpness" />
      <div>
        <div className="flex items-center justify-between text-[11px] mb-0.5">
          <span className="text-slate-600">Exposure</span><span className="font-mono text-slate-800">{ex > 0 ? "+" : ""}{ex}%</span>
        </div>
        <Slider value={[ex]} min={-100} max={100} step={1} onValueChange={(v) => setEx(v[0])} className="mt-0.5" data-testid="adj-exposure" />
      </div>
      <div className="flex gap-1.5">
        <Button size="sm" variant="outline" className="flex-1 text-xs h-8" onClick={reset} data-testid="adj-reset">Reset</Button>
        <Button size="sm" onClick={runAdjust} disabled={busy || !source} className="flex-1 h-8 bg-orange-600 hover:bg-orange-700 text-white text-xs" data-testid="adj-apply">Apply</Button>
      </div>

      <div className="border-t border-slate-100 pt-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Rotate · Flip · Straighten</div>
        <div className="grid grid-cols-4 gap-1.5">
          {[0, 90, 180, 270].map((r) => (
            <button key={r} onClick={() => setRot(r)} data-testid={`rot-${r}`}
              className={`h-8 rounded-md text-[11px] font-semibold ${rot === r ? "bg-orange-500 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`}>{r}°</button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-1.5 mt-2">
          <button onClick={() => setFlipH(!flipH)} data-testid="flip-h" className={`h-8 rounded-md text-[11px] font-medium ${flipH ? "bg-orange-500 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`}>Flip ⇄</button>
          <button onClick={() => setFlipV(!flipV)} data-testid="flip-v" className={`h-8 rounded-md text-[11px] font-medium ${flipV ? "bg-orange-500 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`}>Flip ⇅</button>
        </div>
        <div className="mt-2">
          <div className="flex items-center justify-between text-[11px] mb-0.5">
            <span className="text-slate-600">Straighten</span><span className="font-mono text-slate-800">{straight}°</span>
          </div>
          <Slider value={[straight]} min={-45} max={45} step={0.5} onValueChange={(v) => setStraight(v[0])} data-testid="straighten" />
        </div>
        <Button size="sm" onClick={runTransform} disabled={busy || !source} className="w-full mt-2 h-9 bg-slate-900 hover:bg-slate-800 text-white text-xs" data-testid="transform-apply">Apply Transform</Button>
      </div>
    </div>
  );
}

// ==================================================================
// Filters
// ==================================================================
function FilterPanel({ source, setOutput, busy, setBusy }) {
  const [f, setF] = useState("bw");
  const options = [
    { id: "bw",      label: "Black & White" },
    { id: "sepia",   label: "Sepia" },
    { id: "vintage", label: "Vintage" },
    { id: "hdr",     label: "HDR" },
    { id: "cool",    label: "Cool Tone" },
    { id: "warm",    label: "Warm Tone" },
  ];
  const run = async () => {
    if (!source) { toast.error("Upload first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/photo/filter", { image: source, filter: f });
      setOutput(data.image); toast.success(`Filter: ${f}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Filters</div>
      <div className="grid grid-cols-2 gap-1.5">
        {options.map((o) => (
          <button key={o.id} onClick={() => setF(o.id)} data-testid={`filter-${o.id}`}
            className={`h-9 rounded-md text-xs font-medium border ${f === o.id ? "bg-orange-50 border-orange-300 text-orange-900" : "bg-white border-slate-200 text-slate-700 hover:border-orange-300"}`}>
            {o.label}
          </button>
        ))}
      </div>
      <Button onClick={run} disabled={busy || !source} className="w-full h-10 bg-orange-600 hover:bg-orange-700 text-white" data-testid="filter-apply">Apply Filter</Button>
    </div>
  );
}

// ==================================================================
// Resize & Compress
// ==================================================================
function ResizePanel({ source, setOutput, busy, setBusy }) {
  const [unit, setUnit] = useState("px");
  const [w, setW] = useState(1200); const [h, setH] = useState(0);
  const [keep, setKeep] = useState(true);
  const [target, setTarget] = useState(100);
  const [meta, setMeta] = useState(null);

  const runResize = async () => {
    if (!source) { toast.error("Upload first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/photo/resize", { image: source, unit, width: Number(w) || 0, height: Number(h) || 0, keep_aspect: keep, dpi: 300 });
      setOutput(data.image); toast.success(`Resized to ${data.size.join(" × ")}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const runCompress = async () => {
    if (!source) { toast.error("Upload first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/photo/compress", { image: source, target_kb: Number(target) });
      setOutput(data.image);
      setMeta({ size_kb: data.size_kb, quality: data.quality, scale: data.scale, achieved: data.achieved });
      toast.success(`Compressed to ${data.size_kb} KB`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Smart Resize</div>
      <Select value={unit} onValueChange={setUnit}>
        <SelectTrigger className="h-9" data-testid="resize-unit"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="px">Pixels</SelectItem>
          <SelectItem value="mm">Millimeters</SelectItem>
          <SelectItem value="cm">Centimeters</SelectItem>
          <SelectItem value="inch">Inches</SelectItem>
        </SelectContent>
      </Select>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] text-slate-500">Width</Label>
          <Input value={w} onChange={(e) => setW(e.target.value)} type="number" min={0} className="h-9" data-testid="resize-w" />
        </div>
        <div>
          <Label className="text-[10px] text-slate-500">Height</Label>
          <Input value={h} onChange={(e) => setH(e.target.value)} type="number" min={0} className="h-9" data-testid="resize-h" />
        </div>
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
        <input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} className="w-4 h-4" data-testid="resize-keep" /> Keep aspect ratio
      </label>
      <Button onClick={runResize} disabled={busy || !source} className="w-full h-9 bg-slate-900 hover:bg-slate-800 text-white text-xs" data-testid="resize-apply">Resize</Button>

      <div className="border-t border-slate-100 pt-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Compress to Target Size</div>
        <div className="grid grid-cols-3 gap-1.5">
          {COMPRESS_TARGETS.map((kb) => (
            <button key={kb} onClick={() => setTarget(kb)} data-testid={`compress-${kb}`}
              className={`h-8 rounded-md text-[11px] font-semibold ${target === kb ? "bg-orange-500 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`}>
              {kb >= 1024 ? `${kb / 1024} MB` : `${kb} KB`}
            </button>
          ))}
        </div>
        <Input type="number" value={target} onChange={(e) => setTarget(e.target.value)} min={5} className="h-9 mt-2 text-xs" placeholder="Custom KB" data-testid="compress-custom" />
        <Button onClick={runCompress} disabled={busy || !source} className="w-full mt-2 h-10 bg-orange-600 hover:bg-orange-700 text-white gap-1.5" data-testid="compress-apply">
          <FileArrowDown size={12} /> Compress
        </Button>
        {meta && (
          <div className="mt-2 p-2 rounded-md bg-slate-50 border border-slate-200 text-[11px] text-slate-700 space-y-0.5">
            <div>Size: <span className="font-semibold">{meta.size_kb} KB</span> · quality {meta.quality} · scale {meta.scale}</div>
            <div className={meta.achieved ? "text-emerald-700" : "text-amber-700"}>{meta.achieved ? "✓ within target" : "Best-effort (still above target)"}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================================================================
// Passport layout / Batch / Scan — reuse existing pages
// ==================================================================
function LayoutPanel({ source, navigate }) {
  return (
    <div className="space-y-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Passport Layout</div>
      <div className="text-xs text-slate-600">Tile the enhanced photo onto A4 or 4×6 sheets — 2, 4, 6, 8, 12 or 16 photos per sheet.</div>
      <Button className="w-full h-10 bg-slate-900 hover:bg-slate-800 text-white gap-2" onClick={() => navigate("/photo-studio?tab=passport")} data-testid="open-layout">
        <GridFour size={14} /> Open Passport Layout Studio
      </Button>
      {source && (
        <div className="text-[11px] text-slate-500 italic">
          Tip: enhance/format the photo first, use the "Use output" button on the preview, then tile it here.
        </div>
      )}
    </div>
  );
}
function BatchLink({ navigate }) {
  return (
    <div className="space-y-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Batch Processing</div>
      <div className="text-xs text-slate-600">Enhance up to 20 photos at once with the same AI tool. Download all as ZIP.</div>
      <Button className="w-full h-10 bg-slate-900 hover:bg-slate-800 text-white gap-2" onClick={() => navigate("/photo-studio?tab=bulk")} data-testid="open-batch">
        <Stack size={14} /> Open Batch Studio
      </Button>
    </div>
  );
}
function ScanLink({ navigate }) {
  return (
    <div className="space-y-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Scan &amp; OCR Center</div>
      <div className="text-xs text-slate-600">Auto-crop &amp; deskew documents, extract text in English / Hindi / Marathi, and export searchable PDFs.</div>
      <Button className="w-full h-10 bg-slate-900 hover:bg-slate-800 text-white gap-2" onClick={() => navigate("/photo-studio?tab=scan")} data-testid="open-scan">
        <Scan size={14} /> Open Scan Center
      </Button>
    </div>
  );
}

// ==================================================================
// History
// ==================================================================
function HistoryPanel({ stats, recent }) {
  const TOOL_LABELS = {
    bg_remove: "Background Removed", hd_enhance: "AI HD Enhance", hd_upscale: "HD Upscale (4×)",
    upscale_2x: "Super Res 2×", upscale_4x: "Super Res 4×", upscale_8x: "Super Res 8×",
    face_restore: "Face Enhancement", portrait_retouch: "Portrait Retouch", restore_old: "Restore Old",
    document_cleanup: "Document Cleanup", remove_blur: "Remove Blur", remove_noise: "Remove Noise",
    sharpen: "Sharpen", clarity: "Clarity Upscale",
    adjust: "Adjustments", filter: "Filter Applied", transform: "Transform", resize: "Resized", compress: "Compressed",
    bg_color: "BG Colour", gov_preset: "Gov Preset", save_to_customer: "Saved to Customer",
  };
  return (
    <div className="space-y-3" data-testid="history-panel">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Today's Jobs</div>
      <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 bg-orange-50">
          <span className="text-xs font-semibold text-orange-900">Total processed</span>
          <span className="text-lg font-bold text-orange-600">{stats.total}</span>
        </div>
        {Object.entries(stats.by_tool || {}).sort((a, b) => b[1] - a[1]).slice(0, 12).map(([k, v]) => (
          <div key={k} className="flex items-center justify-between px-3 py-1.5 text-xs">
            <span className="text-slate-700 truncate mr-2">{TOOL_LABELS[k] || k}</span>
            <span className="font-mono font-semibold text-slate-900">{v}</span>
          </div>
        ))}
        {stats.total === 0 && (
          <div className="px-3 py-4 text-center text-[11px] text-slate-400 italic">No jobs yet today — start enhancing!</div>
        )}
      </div>
      {recent.length > 0 && (
        <>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mt-3">Recent this Session</div>
          <div className="grid grid-cols-3 gap-1.5">
            {recent.map((r, i) => (
              <div key={i} className="relative aspect-square rounded-md overflow-hidden border border-slate-200 bg-slate-50">
                {r.out ? <img src={r.out} alt="" className="w-full h-full object-cover" /> : null}
                <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[9px] px-1 py-0.5 truncate">{r.tool}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
